/**
 * Shared chrome for the pre-auth marketing pages (landing, pricing, legal):
 * the scoreboard-style top nav with the fan and team doors, the footer, and
 * the tier cards with a monthly / annual toggle. API-free by design.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { PRODUCT_NAME, ROUTES } from '../../lib/constants';
import {
  annualSavingsDollars,
  contactSalesEmail,
  formatMonthlyPrice,
  TIERS,
  type BillingPeriod,
  type Tier,
} from '../../lib/marketing-content';

import { Football } from './field';

export function MarketingChrome({
  children,
  variant = 'page',
}: {
  children: ReactNode;
  /** `field`: the landing page draws its own full-bleed field under the nav. */
  variant?: 'field' | 'page';
}): JSX.Element {
  const salesEmail = contactSalesEmail();
  return (
    <div className={`marketing marketing--${variant}`}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="marketing__nav">
        <Link to={ROUTES.landing} className="marketing__brand">
          <img src="/images/logo-mark.png" alt="" width={32} height={32} />
          <span>
            GRIDIRON <em>TRACKER</em>
          </span>
        </Link>
        <ul className="marketing__nav-links">
          <li>
            <Link to={`${ROUTES.landing}#play-entry`}>Features</Link>
          </li>
          <li>
            <Link to={ROUTES.pricing}>Pricing</Link>
          </li>
          {salesEmail === null ? null : (
            <li>
              <a href={`mailto:${salesEmail}`}>Contact sales</a>
            </li>
          )}
        </ul>
        <div className="marketing__nav-actions">
          {/* Fans first in the DOM: they are the largest audience and the one
              least able to find their way in. It lands on the fan SIGNUP page:
              a marketing visitor is far more likely to be new than returning,
              and the signup page links to sign-in for the rest. "Team sign in"
              is relabelled from a bare "Sign In" so the two doors are
              unambiguous. */}
          <Link className="btn btn--ghost btn--sm" to={ROUTES.fanSignup}>
            Fans
          </Link>
          <Link className="btn btn--secondary btn--sm" to={ROUTES.login}>
            Team sign in
          </Link>
          <Link className="btn btn--primary btn--sm" to={ROUTES.signup}>
            Start Free Trial
          </Link>
        </div>
      </header>

      <main id="main-content">{children}</main>

      <footer className="marketing__footer">
        <div className="marketing__footer-inner">
          <span className="marketing__footer-brand">
            <Football className="marketing__footer-ball" />
            <span>
              © {new Date().getFullYear()} {PRODUCT_NAME}. Football program data only — never
              student education records.
            </span>
          </span>
          <ul className="marketing__footer-links">
            <li>
              <Link to={ROUTES.terms}>Terms of Service</Link>
            </li>
            <li>
              <Link to={ROUTES.privacy}>Privacy Policy</Link>
            </li>
            {salesEmail === null ? null : (
              <li>
                <a href={`mailto:${salesEmail}`}>{salesEmail}</a>
              </li>
            )}
            <li>
              <Link to={ROUTES.fanSignup}>Follow a team</Link>
            </li>
            <li>
              <Link to={ROUTES.login}>Sign In</Link>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

export function TierCard({
  tier,
  salesEmail,
  period = 'monthly',
}: {
  tier: Tier;
  salesEmail: string | null;
  period?: BillingPeriod;
}): JSX.Element {
  // Without a configured sales address, "Contact Sales" tiers route to signup
  // (trial first, upgrade conversation later) instead of a dead mailto.
  const isContactSales = tier.ctaHref.startsWith('mailto:') && salesEmail !== null;
  const isAnnual = period === 'annual' && tier.annualPriceCents > 0;
  const savings = annualSavingsDollars(tier);
  return (
    <article
      className={`tier-card${tier.isFeatured ? ' tier-card--featured' : ''}`}
      data-tier={tier.name}
    >
      {tier.badge ? <span className="tier-card__badge">{tier.badge}</span> : null}
      <h3>{tier.displayName}</h3>
      <p className="tier-card__price">
        {formatMonthlyPrice(isAnnual ? tier.annualPriceCents : tier.priceCents)}
        <span> {isAnnual ? '/year' : '/month'}</span>
      </p>
      <p className="tier-card__note">
        {isAnnual && savings > 0
          ? `Billed yearly. Saves $${savings.toLocaleString('en-US')} a year.`
          : tier.priceCents === 0
            ? 'Free for good.'
            : `Or ${formatMonthlyPrice(tier.annualPriceCents)} billed yearly.`}
      </p>
      <p className="tier-card__summary">{tier.summary}</p>
      <ul className="tier-card__features">
        {tier.features.map((feature) => (
          <li key={feature.label} data-included={feature.isIncluded ? 'yes' : 'no'}>
            <span className="tier-card__mark" aria-hidden="true">
              {feature.isIncluded ? '✓' : '–'}
            </span>
            <span>
              {feature.label}
              <span className="visually-hidden">
                {feature.isIncluded ? ' included' : ' not included'}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {isContactSales ? (
        <a className="btn btn--secondary" href={`mailto:${salesEmail}`}>
          {tier.ctaLabel}
        </a>
      ) : (
        <Link
          className={`btn ${tier.isFeatured ? 'btn--primary' : 'btn--secondary'}`}
          to={tier.ctaHref.startsWith('mailto:') ? ROUTES.signup : tier.ctaHref}
        >
          {tier.ctaHref.startsWith('mailto:') ? 'Start Free Trial' : tier.ctaLabel}
        </Link>
      )}
    </article>
  );
}

/** Monthly / annual switch for the tier grid. */
function PeriodToggle({
  period,
  onChange,
}: {
  period: BillingPeriod;
  onChange: (next: BillingPeriod) => void;
}): JSX.Element {
  return (
    <div className="period-toggle" role="group" aria-label="Billing period">
      {(['monthly', 'annual'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className="period-toggle__option"
          aria-pressed={period === option}
          onClick={() => onChange(option)}
        >
          {option === 'monthly' ? (
            'Monthly'
          ) : (
            <>
              Annual <span className="period-toggle__save">save over 15%</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}

export function TierGrid(): JSX.Element {
  const salesEmail = contactSalesEmail();
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  return (
    <div className="tiers">
      <PeriodToggle period={period} onChange={setPeriod} />
      <div className="tier-grid">
        {TIERS.map((tier) => (
          <TierCard key={tier.name} tier={tier} salesEmail={salesEmail} period={period} />
        ))}
      </div>
    </div>
  );
}
